import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronRight, FilePenLine, Search, X } from 'lucide-react'

import { UserDisplay } from '@/components'
import { EmptySearchResult } from '@/components/EmptySearchResult'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCompanyById } from '@/features/company/hooks'
import {
  DeliverableSetAction,
  getDeliverableSetListColumns,
} from '@/features/deliverable-set/columns'
import {
  DelegateDialog,
  InviteUsersDialog,
} from '@/features/deliverable-set/components'
import DeleteDeliverableSetDialog from '@/features/deliverable-set/components/DeleteDeliverableSetDialog'
import AddDeliverableSetDialog from '@/features/deliverable-set/components/DeliverableSetDialog'
import { useDeliverableSetList } from '@/features/deliverable-set/hooks'
import { useDeliverableSetsStore } from '@/features/deliverable-set/store'
import ProjectDialog from '@/features/project/components/ProjectDialog'
import ProjectHistoryDrawer from '@/features/project/components/ProjectHistoryDrawer'
import { useProjectById } from '@/features/project/hooks'
import { useProjectsStore } from '@/features/project/store'
import { ERoleType } from '@/features/user-management/constants'
import { useCurrentUser } from '@/hooks/useAuth'
import { useDebounce } from '@/hooks/useDebounce'
import { ResponseDeliverableSet } from '@/schema'
import { SortOrder } from '@/types/common'
import { formatDisplayDate } from '@/utils/common'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '@/utils/constants'
import { buildDeliverableSetRoute, ROUTES } from '@/utils/routes'

import { useAuthStore } from '../../features/auth/index.ts'
import CancelApprovalRequestDialog from '../../features/deliverable-set/components/CancelApprovalRequestDialog.tsx'
import SendToReviewDialog from '../../features/deliverable-set/components/SendToReviewDialog.tsx'
import { isUserHasPermissionInProject } from '../../utils/roleBasedRouting.ts'
const ProjectDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: currentUser } = useCurrentUser()
  const {
    data: project,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
    error,
  } = useProjectById(id)
  const getListProjectUserHasAccess = useAuthStore(
    (state) => state.getListProjectUserHasAccess
  )
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )
  const roles = useAuthStore((state) => state.roles)
  const hasOrgAdminRole = roles?.some(
    (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
  )
  const isTeamMember = useMemo(
    () =>
      roles &&
      roles.length > 0 &&
      roles.every((role) => role.role_name === ERoleType.TEAM_MEMBER),
    [roles]
  )

  const isOrgAdminOrProjectLead = useMemo(
    () =>
      roles?.some(
        (role) =>
          role.role_name === ERoleType.ORGANIZATION_ADMIN ||
          role.role_name === ERoleType.PROJECT_LEAD
      ),
    [roles]
  )

  const canViewHistory =
    hasOrgAdminRole ||
    roles?.some((role) => role.role_name === ERoleType.PROJECT_LEAD)
  const [historyOpen, setHistoryOpen] = useState<boolean>(false)

  const listProject = getListProjectUserHasAccess()
  const hasPermission = isUserHasPermissionInProject(listProject, id || '')

  useEffect(() => {
    if (!hasPermission && !hasOrgAdminRole) {
      navigate(ROUTES.UNAUTHORIZED, { replace: true })
    }
  }, [hasOrgAdminRole, hasPermission, id, navigate])

  const {
    filters,
    page,
    rowsPerPage,
    sort,
    setPage,
    setSort,
    setEditingDeliverableSet,
    setDialogOpen,
    setDeletingDeliverableSet,
    setDeleteDialogOpen,
    setSendToReviewDeliverableSet,
    setCancelApprovalDeliverableSet,
    setDelegateDeliverableSet,
    reset: resetDeliverableSetsStore,
  } = useDeliverableSetsStore()

  const { setEditingProject, setDialogOpen: setProjectDialogOpen } =
    useProjectsStore()

  const [localSearch, setLocalSearch] = useState<string>(filters.searchName)
  const debouncedSearchText = useDebounce(localSearch, SEARCH_DEBOUNCE_DELAY_MS)

  const debouncedSearch = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  // Only fetch company from API if user is admin
  // Pass empty string to disable the query when user is not admin
  const { data: company, isLoading: isLoadingCompany } = useCompanyById(
    hasOrgAdminRole && project?.company_id ? project.company_id : ''
  )

  const projectFilters = useMemo(() => {
    return {
      ...filters,
      projectId: project?.id || null,
    }
  }, [filters, project?.id])

  const {
    data: { data: deliverableSets = [], pagination } = {},
    isLoading: isLoadingDeliverableSets,
  } = useDeliverableSetList({
    search: debouncedSearch,
    page,
    rowsPerPage,
    sort: sort.column && sort.direction ? sort : undefined,
    filters: projectFilters,
  })

  useEffect(() => {
    resetDeliverableSetsStore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset page to 0 when search changes
  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, setPage])

  // Reset page to 0 when sort changes
  useEffect(() => {
    setPage(0)
  }, [sort.column, sort.direction, setPage])

  // Empty state message for search results
  const noDataMessage = useMemo(() => {
    const isSearching = Boolean(debouncedSearch)
    if (isSearching) {
      return <EmptySearchResult translationKey="deliverableSet" />
    }
    return null
  }, [debouncedSearch])

  // Use API pagination instead of client-side pagination
  const totalPages = useMemo(() => {
    return Math.ceil((pagination?.total_items ?? DEFAULT_PAGE) / rowsPerPage)
  }, [pagination?.total_items, rowsPerPage])

  const handleActionClick = useCallback(
    (action: DeliverableSetAction, item: ResponseDeliverableSet) => {
      switch (action) {
        case 'view-detail':
          navigate(buildDeliverableSetRoute(item.id))
          break
        case 'edit':
          setEditingDeliverableSet(item)
          setDialogOpen(true)
          break
        case 'delete':
          setDeletingDeliverableSet(item)
          setDeleteDialogOpen(true)
          break
        case 'sendApproval':
          setSendToReviewDeliverableSet(item)
          break
        case 'cancelApproval':
          setCancelApprovalDeliverableSet(item)
          break
        case 'delegateReview':
          setDelegateDeliverableSet(item)
          break
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navigate,
      setEditingDeliverableSet,
      setDialogOpen,
      setDeletingDeliverableSet,
      setDeleteDialogOpen,
      setSendToReviewDeliverableSet,
      setCancelApprovalDeliverableSet,
    ]
  )

  const columns = useMemo(
    () =>
      getDeliverableSetListColumns({
        handleActionClick,
        t,
        isFetching: isFetchingProject,
        currentUserId: currentUser?.id,
        isTeamMember,
        isOrgAdminOrProjectLead,
      }),
    [
      currentUser?.id,
      handleActionClick,
      isFetchingProject,
      isOrgAdminOrProjectLead,
      isTeamMember,
      t,
    ]
  )

  const companyContent = () => {
    if (isLoadingCompany) {
      return (
        <span className="font-semibold text-secondary-500">
          {t('common.loading')}
        </span>
      )
    }

    // For admin users, use API data
    if (hasOrgAdminRole) {
      if (company && company.length > 0) {
        return (
          <Link
            to={`${ROUTES.COMPANY}/${project?.company_id}`}
            className="font-semibold no-underline break-words text-brand-tertiary-600"
          >
            {company[0].name}
          </Link>
        )
      }
      return <span className="font-semibold text-secondary-500">-</span>
    }

    // For non-admin users, get company from roles and display as plain text
    // (they don't have permission to view company detail page)
    const companiesFromRoles = getCompaniesFromRoles()
    const companyFromRoles = companiesFromRoles.find(
      (c) => c.id === project?.company_id
    )

    if (companyFromRoles) {
      return (
        <span className="font-semibold break-words text-primary-900">
          {companyFromRoles.name}
        </span>
      )
    }

    return <span className="font-semibold text-secondary-500">-</span>
  }

  if (isLoadingProject) {
    return (
      <div className="flex justify-center items-center min-h-50">
        <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('project.page.detailTitle')}
          </h1>
          <p className="m-0 text-red-400 text-md">
            {t('project.page.notFound')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Link
            to={ROUTES.PROJECT}
            className="flex-shrink-0 font-semibold no-underline text-quaternary-500 hover:text-quaternary-600"
          >
            {t('sidebar.subItem.project')}
          </Link>
          <ChevronRight
            size={16}
            className="flex-shrink-0 text-gray-light-300"
          />
          <span className="min-w-0 font-semibold break-all text-primary-900">
            {project.project_name}
          </span>
        </div>
        {canViewHistory && (
          <Button
            variant="outlined"
            className="text-sm text-quaternary-600"
            onClick={() => setHistoryOpen(true)}
          >
            {t('project.actions.history')}
          </Button>
        )}
      </div>

      {/* Page Title */}
      <h1 className="text-display-xs font-semibold !mt-4 break-all">
        {project.project_name}
      </h1>

      <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="m-0 text-lg font-bold text-primary-900">
            {t('project.detail.overview')}
          </h2>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              {!isTeamMember && (
                <Button
                  variant="text"
                  className="bg-transparent border-none w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
                  onClick={() => {
                    setEditingProject(project)
                    setProjectDialogOpen(true)
                  }}
                  disabled={isFetchingProject}
                >
                  <FilePenLine size={20} />
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('common.edit')}</span>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator />

        <div className="grid grid-cols-3 gap-4 px-6 py-4 text-sm">
          <div className="flex flex-col">
            <span className="font-medium text-secondary-700">
              {t('project.detail.name')}
            </span>
            <span className="text-xs font-semibold break-words text-primary-900">
              {project.project_name}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-secondary-700">
              {t('project.detail.createBy')}
            </span>
            <span className="text-xs font-semibold break-words text-primary-900">
              <UserDisplay user={project.created_by_user} showAvatar={false} />
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-secondary-700">
              {t('project.detail.createDate')}
            </span>
            <span className="text-xs font-medium break-words text-primary-900">
              {formatDisplayDate(project.created_at)}
            </span>
          </div>

          <div className="flex flex-col text-sm font-medium">
            <span className="text-secondary-700">
              {t('project.detail.company')}
            </span>
            {companyContent()}
          </div>

          <div className="flex flex-col text-sm font-medium">
            <span className="text-secondary-700">
              {t('project.detail.reportingEvent')}
            </span>
            <span className="text-sm font-semibold break-words text-primary-900">
              {project.reporting_events && project.reporting_events.length > 0
                ? project.reporting_events
                    .map((event) => event.reporting_event_name)
                    .join(', ')
                : '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm border-secondary rounded-xl">
        <div className="flex items-center justify-between border-t border-b-0 border-l border-r border-solid border-[--Colors-Gray-light-300] rounded-t-xl">
          <div className="px-[--spacing-3xl] py-[--spacing-xl] flex items-center justify-between w-full">
            <h2 className="m-0 text-lg font-semibold text-primary-900">
              {t('sidebar.subItem.deliverableSet')}
            </h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t('deliverableSet.filters.search')}
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  className="w-64"
                  leadingIcon={<Search size={20} className="text-gray-400" />}
                  trailingIcon={
                    localSearch ? (
                      <X className="w-3.5 h-3.5 cursor-pointer" />
                    ) : undefined
                  }
                  onTrailingIconClick={
                    localSearch ? () => setLocalSearch('') : undefined
                  }
                />
              </div>
              {!isTeamMember && (
                <Button
                  variant="solid"
                  onClick={() =>
                    setDialogOpen(true, project.company_id, project.id)
                  }
                  className="text-sm"
                >
                  {t('deliverableSet.actions.addNew')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <Table
          data={deliverableSets}
          columns={columns}
          isLoading={isLoadingDeliverableSets}
          page={page}
          noDataMessage={noDataMessage}
          totalPages={totalPages}
          onPageChange={setPage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={(
            column: keyof ResponseDeliverableSet | null,
            direction: SortOrder
          ) => setSort({ column, direction })}
          onRowClick={(row) => handleActionClick('view-detail', row)}
        />
      </div>

      <AddDeliverableSetDialog />
      <DeleteDeliverableSetDialog />
      <ProjectDialog />
      <SendToReviewDialog />
      <CancelApprovalRequestDialog />
      <DelegateDialog />
      <InviteUsersDialog />
      {canViewHistory && (
        <ProjectHistoryDrawer
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          entityId={id || ''}
        />
      )}
    </div>
  )
}

export default ProjectDetailPage
